import { useEffect, useMemo, useState } from 'react';
import {
  computePendingWithdrawalCredits,
  computeWithdrawableCredits,
  validateListingDraft,
  validateWithdrawalDraft,
} from '../domain/marketplacePolicy';
import {
  Listing,
  ListingDraft,
  ManagedView,
  TradeRecord,
  WithdrawalDraft,
  WithdrawalRecord,
} from '../domain/marketplaceTypes';
import { authService } from '../services/auth';
import { loadMarketplaceSnapshot, saveMarketplaceSnapshot } from '../services/marketplaceStorage';
import {
  createMarketplaceListing,
  fetchMarketplaceState,
  requestMarketplaceWithdrawal,
  purchaseMarketplaceListing,
  releaseTrade,
  disputeTrade,
} from '../services/marketplaceApi';

type EngineResult = {
  listings: Listing[];
  trades: TradeRecord[];
  withdrawals: WithdrawalRecord[];
  isSyncing: boolean;
  managedView: ManagedView;
  setManagedView: (view: ManagedView) => void;
  createListing: (draft: ListingDraft, actionKey: string) => Promise<{ ok: boolean; message: string }>;
  requestWithdrawal: (draft: WithdrawalDraft, actionKey: string) => Promise<{ ok: boolean; message: string }>;
  availableWithdrawCredits: number;
  pendingWithdrawCredits: number;
  canWithdrawCredits: number;
  sellingProducts: Listing[];
  boughtProducts: TradeRecord[];
  purchaseListing: (listingId: string) => Promise<{ ok: boolean; message: string }>;
  releaseTradeFund: (tradeId: string) => Promise<{ ok: boolean; message: string }>;
  disputeTradeFund: (tradeId: string, reason: string) => Promise<{ ok: boolean; message: string }>;
};

export function useMarketplaceEngine(): EngineResult {
  const businessId = authService.getBusinessId();
  const [state, setState] = useState(() => loadMarketplaceSnapshot(businessId));
  const [isSyncing, setIsSyncing] = useState(false);
  const [managedView, setManagedView] = useState<ManagedView>('selling');

  const persist = (next: typeof state) => {
    setState(next);
    saveMarketplaceSnapshot(businessId, next);
  };

  useEffect(() => {
    let cancelled = false;

    const syncFromServer = async () => {
      setIsSyncing(true);
      try {
        const snapshot = await fetchMarketplaceState();
        if (cancelled) return;
        setState(snapshot);
        saveMarketplaceSnapshot(businessId, snapshot);
      } catch {
        // Keep local fallback snapshot when API is unavailable.
      } finally {
        if (!cancelled) setIsSyncing(false);
      }
    };

    void syncFromServer();

    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const createListing = async (draft: ListingDraft, actionKey: string) => {
    const validationError = validateListingDraft(draft);
    if (validationError) return { ok: false, message: validationError };
    if (state.actionKeys.includes(actionKey)) {
      return { ok: false, message: 'Yêu cầu đã được xử lý trước đó (idempotency guard).' };
    }

    try {
      const result = await createMarketplaceListing(draft, actionKey);
      persist(result.state);
      return { ok: true, message: result.message };
    } catch (error) {
      // fallback to local mode when API is temporarily unavailable
    }

    const now = new Date().toLocaleString('vi-VN');
    const created: Listing = {
      id: `mk-${Date.now()}`,
      title: draft.title.trim(),
      type: draft.type,
      platform: draft.platform,
      seller: 'Bạn',
      credits: Number(draft.credits),
      banner: draft.banner.trim(),
      description: draft.description.trim(),
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    };

    const next = {
      ...state,
      listings: [created, ...state.listings],
      actionKeys: [actionKey, ...state.actionKeys].slice(0, 100),
    };
    persist(next);
    return { ok: true, message: 'Đã tạo sản phẩm ở trạng thái chờ duyệt minh bạch (pending).' };
  };

  const requestWithdrawal = async (draft: WithdrawalDraft, actionKey: string) => {
    const validationError = validateWithdrawalDraft(draft, state.trades, state.withdrawals);
    if (validationError) return { ok: false, message: validationError };
    if (state.actionKeys.includes(actionKey)) {
      return { ok: false, message: 'Yêu cầu đã được xử lý trước đó (idempotency guard).' };
    }

    try {
      const result = await requestMarketplaceWithdrawal(draft, actionKey);
      persist(result.state);
      return { ok: true, message: result.message };
    } catch (error) {
      // fallback to local mode when API is temporarily unavailable
    }

    const created: WithdrawalRecord = {
      id: `wd-${Date.now()}`,
      amount: Number(draft.amount),
      status: 'pending',
      destination: draft.destination.trim(),
      createdAt: new Date().toLocaleString('vi-VN'),
      requestRef: `WDR-${Date.now()}`,
    };

    const next = {
      ...state,
      withdrawals: [created, ...state.withdrawals],
      actionKeys: [actionKey, ...state.actionKeys].slice(0, 100),
    };
    persist(next);
    return { ok: true, message: 'Đã tạo yêu cầu rút tiền, chờ kiểm duyệt để đảm bảo an toàn giao dịch 2 bên.' };
  };

  const purchaseListing = async (listingId: string) => {
    try {
      const result = await purchaseMarketplaceListing(listingId);
      // Re-sync to get the updated trade state
      const snapshot = await fetchMarketplaceState();
      persist(snapshot);
      return { ok: true, message: result.message };
    } catch (error: any) {
      return { ok: false, message: error.message };
    }
  };

  const releaseTradeFund = async (tradeId: string) => {
    try {
      const result = await releaseTrade(tradeId);
      const snapshot = await fetchMarketplaceState();
      persist(snapshot);
      return { ok: true, message: result.message };
    } catch (error: any) {
      return { ok: false, message: error.message };
    }
  };

  const disputeTradeFund = async (tradeId: string, reason: string) => {
    try {
      const result = await disputeTrade(tradeId, reason);
      const snapshot = await fetchMarketplaceState();
      persist(snapshot);
      return { ok: true, message: result.message };
    } catch (error: any) {
      return { ok: false, message: error.message };
    }
  };

  const availableWithdrawCredits = useMemo(() => computeWithdrawableCredits(state.trades), [state.trades]);
  const pendingWithdrawCredits = useMemo(
    () => computePendingWithdrawalCredits(state.withdrawals),
    [state.withdrawals],
  );
  const canWithdrawCredits = Math.max(availableWithdrawCredits - pendingWithdrawCredits, 0);

  const sellingProducts = useMemo(
    () => state.listings.filter((item) => item.seller === 'Bạn'),
    [state.listings],
  );

  const boughtProducts = useMemo(
    () => state.trades.filter((item) => item.side === 'buy'),
    [state.trades],
  );

  return {
    listings: state.listings,
    trades: state.trades,
    withdrawals: state.withdrawals,
    isSyncing,
    managedView,
    setManagedView,
    createListing,
    requestWithdrawal,
    availableWithdrawCredits,
    pendingWithdrawCredits,
    canWithdrawCredits,
    sellingProducts,
    boughtProducts,
    purchaseListing,
    releaseTradeFund,
    disputeTradeFund,
  };
}
